import { ReactElement, useMemo } from 'react'
import {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType
} from 'next'

import Meta from '@/components/base/meta'
import PageBuilder from '@/components/layout/builder'
import Layout from '@/components/layout/layout'
import { useStore } from '@/contexts/store'
import {
  normalizeAncestorTaxonomyData,
  normalizeChildrenTaxonomyData,
  normalizeHeadData
} from '@/utils/page-data'
import { getPromiseFromFetch } from '@/utils/request'
import { API } from '@/constants'

import { WPPageData, WPRoutes } from '@/types'

export default function Page(
  pageProps: InferGetStaticPropsType<typeof getStaticProps>
) {
  const { strings } = useStore()
  const output = useMemo(() => {
    if (pageProps.type === 'taxonomy' && pageProps.object.parent === 0) {
      return (
        <PageBuilder
          pageProps={normalizeAncestorTaxonomyData(pageProps, strings)}
        />
      )
    }
    if (pageProps.type === 'taxonomy' && pageProps.object.parent !== 0) {
      return (
        <PageBuilder pageProps={normalizeChildrenTaxonomyData(pageProps)} />
      )
    }
    return <PageBuilder pageProps={pageProps} />
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageProps])

  return output
}

Page.getLayout = function getLayout(page: ReactElement, pageProps: WPPageData) {
  return (
    <Layout
      pageProps={pageProps.type === 'taxonomy' ? pageProps.object : pageProps}
    >
      <Meta pageProps={normalizeHeadData(pageProps)} />
      {page}
    </Layout>
  )
}

export async function getStaticProps({
  params,
  locale,
  defaultLocale
}: GetStaticPropsContext<{ routes: string[] }>) {
  const res = await getPromiseFromFetch<WPPageData>({
    method: 'get',
    url: `/${locale === defaultLocale ? '' : `${locale}/`}${params?.routes.join('/')}/`
  })

  if (res.status === 404) return { notFound: true }

  const { data: pageProps } = res

  return {
    props: {
      ...pageProps
    }
  }
}

export async function getStaticPaths({ defaultLocale }: GetStaticPathsContext) {
  const { data: routes } = await getPromiseFromFetch<WPRoutes>({
    method: 'get',
    url: API.ROUTES
  })

  return {
    paths: routes.map((route) => ({
      params: { routes: route.routes.filter((item) => item !== route.locale) },
      locale: route.locale === defaultLocale ? '' : route.locale
    })),
    fallback: 'blocking' // false
  }
}
